import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from '@mui/material'
import {
  ExpandMore,
  ExpandLess,
  ArrowForward,
  ArrowBack,
  Assignment,
  Email,
  HourglassEmpty,
  NoteAdd,
  Note,
  ViewList,
  Subject,
  Refresh,
  ErrorOutline,
  Image as ImageIcon,
  AttachFile,
  Close,
  PictureAsPdf,
} from '@mui/icons-material'
import yaml from 'js-yaml'
import MessageContent from './MessageContent'
import FormRenderer from './FormRenderer'
import { useConfig } from '../contexts/ConfigContext'
import { useAuth } from '../hooks/useAuth'

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg']
const PDF_EXTENSIONS = ['.pdf']

const UploadedFilePreview = ({ content, ticket }) => {
  const config = useConfig()
  const { getAccessToken } = useAuth()
  const [previewUrl, setPreviewUrl] = useState(null)
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const blobUrlRef = useRef(null)
  const fetchedRef = useRef(false)

  const uploadMatch = content?.match(/User has uploaded file (.+)/)
  const filePath = uploadMatch ? uploadMatch[1].trim() : null
  const isImage = filePath && IMAGE_EXTENSIONS.some(ext => filePath.toLowerCase().endsWith(ext))
  const isPdf = filePath && PDF_EXTENSIONS.some(ext => filePath.toLowerCase().endsWith(ext))

  const fileName = filePath ? filePath.split('/').pop() : null

  // Search for the attachment across the ticket and all child tickets
  // (after workflow completion, displayTicket is the parent but the file may be on a child)
  const findAttachment = () => {
    if (!fileName) return { attachment: null, ownerTicketId: null }
    const match = (att) =>
      att.filename?.includes(fileName) ||
      att.azure_blob_path?.includes(fileName) ||
      att.original_filename?.includes(fileName)

    // Check current ticket first
    const parentAtts = ticket?.data?.external_attachments || []
    const found = parentAtts.find(match)
    if (found) return { attachment: found, ownerTicketId: ticket.id }

    // Check child tickets
    for (const child of (ticket?.child_tickets || [])) {
      const childAtts = child?.data?.external_attachments || []
      const childFound = childAtts.find(match)
      if (childFound) return { attachment: childFound, ownerTicketId: child.id }
    }
    return { attachment: null, ownerTicketId: null }
  }
  const { attachment: matchingAttachment, ownerTicketId } = findAttachment()

  const attachmentId = matchingAttachment?.id
  const ticketId = ownerTicketId

  // Fetch file from API — cached to avoid re-fetching on re-renders
  const fetchFile = async () => {
    if (blobUrlRef.current || fetchedRef.current) return blobUrlRef.current
    if (!attachmentId || !ticketId) return null
    fetchedRef.current = true
    setLoading(true)
    try {
      const token = await getAccessToken()
      if (!token) return null
      const baseUrl = config?.api?.baseUrl || '/api/v1'
      const url = `${baseUrl}/tickets/${ticketId}/attachments/${attachmentId}/download`
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (!response.ok) return null
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      blobUrlRef.current = objectUrl
      setPreviewUrl(objectUrl)
      return objectUrl
    } catch {
      return null
    } finally {
      setLoading(false)
    }
  }

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
    }
  }, [])

  // Images: fetch on mount for inline thumbnail
  useEffect(() => {
    if (isImage && attachmentId) {
      fetchFile()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentId])

  if (!uploadMatch || !attachmentId) return null

  const displayName = matchingAttachment.original_filename || matchingAttachment.filename

  // Image preview: clickable thumbnail that opens full-size dialog
  if (isImage) {
    return (
      <>
        <Box
          sx={{ mt: 1, cursor: previewUrl ? 'pointer' : 'default' }}
          onClick={() => previewUrl && setDialogOpen(true)}
        >
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt={displayName}
              sx={{
                maxWidth: '100%',
                maxHeight: 200,
                borderRadius: 1,
                display: 'block',
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <ImageIcon sx={{ fontSize: '1.2rem' }} />
              <Typography variant="caption">{displayName}</Typography>
            </Box>
          )}
        </Box>
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg">
          <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
            <Typography variant="subtitle2" noWrap>{displayName}</Typography>
            <IconButton size="small" onClick={() => setDialogOpen(false)}><Close /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 1 }}>
            {previewUrl && (
              <Box
                component="img"
                src={previewUrl}
                alt={displayName}
                sx={{ maxWidth: '100%', maxHeight: '80vh', display: 'block', mx: 'auto' }}
              />
            )}
          </DialogContent>
        </Dialog>
      </>
    )
  }

  // PDF: clickable card, fetch on click, open in dialog
  if (isPdf) {
    const handleClick = async () => {
      await fetchFile()
      setDialogOpen(true)
    }

    return (
      <>
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            color: 'text.secondary',
            p: 1,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            maxWidth: 300,
            '&:hover': { bgcolor: 'action.hover' },
          }}
          onClick={handleClick}
        >
          <PictureAsPdf color="error" />
          <Typography variant="body2" noWrap sx={{ flex: 1 }}>{displayName}</Typography>
          {loading && <CircularProgress size={16} />}
        </Box>
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
            <Typography variant="subtitle2" noWrap>{displayName}</Typography>
            <IconButton size="small" onClick={() => setDialogOpen(false)}><Close /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: '80vh' }}>
            {previewUrl && (
              <iframe
                src={previewUrl}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title={displayName}
              />
            )}
          </DialogContent>
        </Dialog>
      </>
    )
  }

  // Other file types: simple indicator
  return (
    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
      <AttachFile sx={{ fontSize: '1.2rem' }} />
      <Typography variant="caption">
        File attached: {displayName} — view in Attachments
      </Typography>
    </Box>
  )
}

// Format a raw value to be human-friendly (sentence case, remove underscores/hyphens)
const formatValue = (val) => {
  const str = String(val);
  // Replace underscores and hyphens with spaces, then sentence-case
  const cleaned = str.replace(/[_-]/g, ' ').trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

// Helper to extract a compact summary from submitted form data
const getFormSummary = (content, { markdown = true } = {}) => {
  try {
    const match = content?.match(/```submitted_form_data\n([\s\S]*?)\n```/);
    if (!match) return null;
    const data = JSON.parse(match[1]);
    if (!data?.values) return null;

    const fields = data.form_spec?.fields;
    const entries = Object.entries(data.values)
      .filter(([, value]) => value !== null && value !== '' && value !== undefined)
      .map(([key, value]) => {
        const fieldSpec = fields?.find(f => f.id === key);
        const label = fieldSpec?.label || key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

        let displayValue;
        const isTableField = fieldSpec?.type === 'table' ||
          (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null);

        if (isTableField && Array.isArray(value)) {
          const columns = fieldSpec?.columns || [];
          const rowStrings = value.map(row => {
            if (columns.length > 0) {
              return columns.map(col => row[col.id] ?? '').filter(Boolean).join(' | ');
            }
            return Object.values(row).filter(v => v !== null && v !== undefined && v !== '').join(' | ');
          });
          displayValue = markdown
            ? '\n' + rowStrings.map(r => `  - ${r}`).join('\n')
            : rowStrings.join('; ');
        } else if (Array.isArray(value)) {
          displayValue = value.map(formatValue).join(', ');
        } else {
          displayValue = formatValue(value);
        }

        return { label, displayValue };
      });

    if (entries.length === 0) return markdown ? '*Form submitted*' : 'Form submitted';

    const useBullets = markdown && entries.length > 1;
    const visible = entries.slice(0, 4);
    const remaining = entries.length - visible.length;
    const formatted = visible.map(({ label, displayValue }) =>
      markdown ? `${useBullets ? '- ' : ''}**${label}:** ${displayValue}` : `${label}: ${displayValue}`
    );
    const separator = markdown ? '\n' : ' | ';
    let result = formatted.join(separator);
    if (remaining > 0) result += `${separator}${useBullets ? '- ' : ''}+ ${remaining} more`;
    return result;
  } catch {
    return null;
  }
};

// Memoized message component to prevent unnecessary re-renders and preserve iframe state
const ConversationMessage = React.memo(({
  message,
  index,
  allMessages,
  isExpanded,
  toggleMessageExpansion,
  formatDate,
  handleFormSubmit,
  formDataByFormId,
  handleFormFieldChange,
  user,
  theme,
  extractMemoryKeys,
  hasApiTag,
  canAddNotes,
  hasNotes,
  hoveredMessageIndex,
  setHoveredMessageIndex,
  handleOpenNotesModal,
  parsedFormSpec,
  showSystemInfo,
  ticket,
  parentTicket,
  expandedForms,
  setExpandedForms,
  isLastVisibleMessage,
  onRetryMessage,
}) => {
  const [isBubbleHovered, setIsBubbleHovered] = React.useState(false);

  // Check for role-override in messageMetadata first, then fall back to actual role
  const effectiveRole = message.messageMetadata?.['role-override'] || message.role;
  const isAgent = effectiveRole?.toLowerCase() === 'assistant' || effectiveRole?.toLowerCase() === 'agent' || effectiveRole?.toLowerCase() === 'system';
  const isUser = effectiveRole?.toLowerCase() === 'user';
  const isExternal = effectiveRole?.toLowerCase() === 'external';
  const isMemoryMessage = message.messageMetadata?.tags?.includes('Memory');
  const isKnowledgeMessage = message.messageMetadata?.tags?.includes('Knowledge');
  const isCaptureFactMessage = message.messageMetadata?.tags?.includes('CaptureFact');
  const isEmailAction = message.messageMetadata?.tags?.includes('EmailAction');
  const isHandoverMessage = message.messageMetadata?.tags?.includes('Handover') || message.messageMetadata?.tags?.includes('ChildTicket');
  const isHandbackMessage = message.messageMetadata?.tags?.includes('AgentHandback') || message.messageMetadata?.tags?.includes('ParentTicket');
  const isCompleteMessage = message.messageMetadata?.tags?.includes('Complete');
  const isSubmittedForm = message.content?.includes('```submitted_form_data');

  // Quick-choice history: a quick_replies prompt is "historical" once a user message
  // follows it. allMessages is filtered for non-admins, so order by originalIndex
  // (array position != originalIndex), not by array slice.
  const isQuickReplyPrompt = message.content?.includes('```quick_replies');
  const laterUserMessages = isQuickReplyPrompt
    ? allMessages
        .filter(m => m.originalIndex > index && m.role === 'user')
        .sort((a, b) => a.originalIndex - b.originalIndex)
    : [];
  const isHistoricalQuickReply = isQuickReplyPrompt && laterUserMessages.length > 0;
  const selectedReply = isHistoricalQuickReply ? laterUserMessages[0].content : null;

  // Calculate previous message early (needed for child chip logic)
  const prevMessage = index > 0 ? allMessages[index - 1] : null

  // Check if this is a child ticket message
  const isChildMessage = message.messageMetadata?.tags?.includes('is_child');
  const childTicketType = isChildMessage ?
    message.messageMetadata?.tags?.split(',')
      .map(t => t.trim())
      .find(t => t !== 'is_child' && !['Memory', 'Knowledge', 'API', 'api', 'EmailAction', 'Handover', 'Complete', 'AgentHandback', 'SubmittedForm'].includes(t))
    : null;

  // Check if previous message is from the same child agent
  const prevIsChildMessage = prevMessage?.messageMetadata?.tags?.includes('is_child');
  const prevChildTicketType = prevIsChildMessage ?
    prevMessage.messageMetadata?.tags?.split(',')
      .map(t => t.trim())
      .find(t => t !== 'is_child' && !['Memory', 'Knowledge', 'API', 'api', 'EmailAction', 'Handover', 'Complete', 'AgentHandback', 'SubmittedForm'].includes(t))
    : null;

  // Only show the child ticket chip if this is the first message from this child agent
  const showChildChip = isChildMessage && childTicketType !== prevChildTicketType;

  // Calculate timestamp logic early (BEFORE special message handling)
  const currentTimeString = formatDate(message.timestamp)
  const prevTimeString = prevMessage ? formatDate(prevMessage.timestamp) : null
  const timeDiffMs = prevMessage ? Math.abs(new Date(message.timestamp) - new Date(prevMessage.timestamp)) : Infinity
  const isFirstMessage = index === 0
  const showTimestamp = showSystemInfo
    ? (isFirstMessage || currentTimeString !== prevTimeString || timeDiffMs > 60000 || isUser || isExternal)
    : isLastVisibleMessage

  // Hide a user message that is the direct, exact-match reply to a quick-choice prompt —
  // the highlighted chip on the prompt already shows the chosen option. Free-text replies
  // (no exact match) stay visible. Notes can still be added on the quick-choice message.
  if (isUser && message.content) {
    const earlier = allMessages
      .filter(m => m.originalIndex < index)
      .sort((a, b) => a.originalIndex - b.originalIndex);
    for (let k = earlier.length - 1; k >= 0; k--) {
      const m = earlier[k];
      if (m.role === 'user') break; // a user message sits between → not a direct reply
      const qrMatch = m.content?.match(/```quick_replies\n([\s\S]*?)(?=\n```|```|$)/);
      if (qrMatch) {
        try {
          const options = yaml.load(qrMatch[1]);
          if (Array.isArray(options) && options.map(String).includes(message.content)) {
            return null; // redundant echo of the chosen option
          }
        } catch { /* malformed YAML — keep message visible */ }
        break; // found the prompt; no exact match → keep visible
      }
    }
  }

  // Skip Memory-tagged messages that contain "Cache Miss:"
  if (isMemoryMessage && message.content?.includes('Cache Miss:')) {
    return null;
  }

  // Special rendering for Memory-tagged messages (left-aligned like assistant messages)
  if (isMemoryMessage) {
    const memoryKeys = extractMemoryKeys(message.content)
    const isSave = message.content?.toLowerCase().includes('saved to memory')
    const memoryAction = isSave ? 'saved to memory' : 'fetched from memory'

    return (
      <Box
        sx={{
          mb: 2,
          ml: (isChildMessage && showSystemInfo) ? 3 : 0,
          pl: (isChildMessage && showSystemInfo) ? 2 : 0,
          borderLeft: (isChildMessage && showSystemInfo) ? '3px solid' : 'none',
          borderLeftColor: (isChildMessage && showSystemInfo) ? 'secondary.main' : 'transparent',
        }}
      >
        <Accordion
          expanded={isExpanded}
          onChange={() => toggleMessageExpansion(index)}
          sx={{
            backgroundColor: theme.palette.mode === 'dark'
              ? 'var(--agentflow-message-agent)'
              : 'transparent',
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            '&:before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary
            sx={{
              minHeight: 'auto',
              p: 2,
              '& .MuiAccordionSummary-content': {
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                position: 'static',
                transform: 'none',
                marginLeft: 0,
              },
              '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                transform: 'rotate(180deg)',
              },
              '&.Mui-expanded': {
                minHeight: 'auto',
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, flex: 1 }}>
              {showTimestamp && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatDate(message.timestamp)}
                </Typography>
              )}
              <Typography variant="body2" sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
              }}>
                {isSave ? 'Saved to' : 'Fetched from'} memory {memoryKeys}
              </Typography>
            </Box>
            <ExpandMore sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
          </AccordionSummary>
          <AccordionDetails sx={{
            pt: 0,
            pb: 2,
            px: 2,
          }}>
            <Box sx={{
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              lineHeight: 1.4,
            }}>
              <MessageContent
                content={message.content}
                isUserMessage={false}
                onFormSubmit={handleFormSubmit}
                formDataByFormId={formDataByFormId}
                onFormFieldChange={handleFormFieldChange}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }

  // Distinct rendering for Handover messages
  if (isHandoverMessage) {
    // Extract the raw agent name from the message content
    const agentNameMatch = message.content.match(/HANDING OVER TO: (.+)/);
    let rawAgentName = agentNameMatch ? agentNameMatch[1] : 'Specialist Agent';

    // Remove markdown formatting (e.g., **text**)
    rawAgentName = rawAgentName.replace(/\*\*/g, '').trim();

    // Format the agent name for display
    let formattedAgentName;
    if (isHandbackMessage) {
      // Handback to parent
      formattedAgentName = 'Orchestrator Agent';
    } else if (rawAgentName.toLowerCase().includes('orchestrator')) {
      formattedAgentName = 'Orchestrator Agent';
    } else {
      // Convert CamelCase to "Title Case Specialist"
      // e.g., "ShipmentNotReceived" -> "Shipment Not Received Specialist"
      formattedAgentName = rawAgentName
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .replace(/\s+/g, ' ')
        + ' Specialist';
    }

    return (
      <Box sx={{
        mb: 3,
        mt: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
          px: 3,
          py: 1.5,
          borderRadius: 10,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            bgcolor: '#f57c00',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <ArrowForward sx={{ width: 18, height: 18, color: 'white' }} />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Handing over to {formattedAgentName}
          </Typography>
        </Box>
        {showTimestamp && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {formatDate(message.timestamp)}
          </Typography>
        )}
      </Box>
    );
  }

  // Distinct rendering for Handback messages - use same banner style as handover
  if (isHandbackMessage) {
    return (
      <Box sx={{
        mb: 3,
        mt: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
          px: 3,
          py: 1.5,
          borderRadius: 10,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            bgcolor: '#f57c00',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <ArrowBack sx={{ width: 18, height: 18, color: 'white' }} />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Handing back to Orchestrator Agent
          </Typography>
        </Box>
        {showTimestamp && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {formatDate(message.timestamp)}
          </Typography>
        )}
      </Box>
    );
  }

  // Simple accordion rendering for Complete messages
  if (isCompleteMessage) {
    return (
      <Box sx={{
        mb: 2,
        ml: (isChildMessage && showSystemInfo) ? 3 : 0,
        pl: (isChildMessage && showSystemInfo) ? 2 : 0,
        borderLeft: (isChildMessage && showSystemInfo) ? '3px solid' : 'none',
        borderLeftColor: (isChildMessage && showSystemInfo) ? 'secondary.main' : 'transparent',
      }}>
        <Accordion
          expanded={isExpanded}
          onChange={() => toggleMessageExpansion(index)}
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? 'var(--agentflow-message-agent)' : 'transparent',
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, flex: 1 }}>
              {showTimestamp && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatDate(message.timestamp)}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                ✓ Workflow completed
              </Typography>
            </Box>
            <ExpandMore sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
            <MessageContent
              content={message.content}
              isUserMessage={false}
              onFormSubmit={handleFormSubmit}
              formDataByFormId={formDataByFormId}
              onFormFieldChange={handleFormFieldChange}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }

  // Special rendering for SubmittedForm messages - same treatment as form placeholders
  if (isSubmittedForm) {
    const isDark = theme.palette.mode === 'dark';
    const messageId = message.id;  // Use actual message ID from backend
    // Use message ID if available, otherwise use index-based key for child messages without IDs
    const expansionKey = messageId || `msg-${index}`;
    const isExpanded = expandedForms.has(expansionKey);

    // Search forms from both current ticket and parent ticket (with children)
    // parentTicket is the root ticket with child_tickets array
    // ticket might be a child ticket if an active child is being displayed

    let formData = null;

    // First try current ticket (could be parent or child)
    if (messageId) {
      formData = ticket?.forms?.find(form => form.id === messageId);
    }

    // If not found and we have a parentTicket, search parent + all children
    if (!formData && parentTicket) {
      // Search parent forms
      if (messageId && parentTicket.forms) {
        formData = parentTicket.forms.find(form => form.id === messageId);
      }

      // Search child ticket forms
      if (!formData && parentTicket.child_tickets) {
        for (const childTicket of parentTicket.child_tickets) {
          // Try to match by message ID (if available)
          if (messageId) {
            formData = childTicket.forms?.find(form => form.id === messageId);
          }
          // Fallback: If message ID is null (child messages), try index-based matching
          if (!formData && !messageId && isChildMessage && childTicketType) {
            // Only match if this child ticket's type matches the message's child type
            if (childTicket.ticket_type === childTicketType || childTicket.type === childTicketType) {
              // Count how many SubmittedForm messages we've seen from THIS child before this one
              const childMessagesBefore = allMessages.slice(0, index).filter(msg =>
                msg.messageMetadata?.tags?.includes('is_child') &&
                msg.messageMetadata?.tags?.includes(childTicketType)
              );
              const childSubmittedFormsBefore = childMessagesBefore.filter(msg =>
                msg.content?.includes('```submitted_form_data')
              );
              const childFormIndex = childSubmittedFormsBefore.length;

              // Get the form at this index from the child ticket
              if (childTicket.forms && childTicket.forms.length > childFormIndex) {
                formData = childTicket.forms[childFormIndex];
              }
            }
          }
          if (formData) break;
        }
      }
    }

    const formSpec = formData ? (typeof formData.form_spec === 'string' ? yaml.load(formData.form_spec) : formData.form_spec) : null;

    // Parse form data from message content for inline FormRenderer
    let inlineFormSpec = null;
    let inlineFormValues = {};
    try {
      const contentMatch = message.content?.match(/```submitted_form_data\n([\s\S]*?)\n```/);
      if (contentMatch) {
        const parsed = JSON.parse(contentMatch[1]);
        if (parsed?.form_spec) {
          inlineFormSpec = typeof parsed.form_spec === 'string' ? yaml.load(parsed.form_spec) : parsed.form_spec;
          inlineFormValues = parsed.values || {};
        }
      }
    } catch { /* ignore parse errors */ }

    const toggleExpand = () => {
      if (!inlineFormSpec) return;
      setExpandedForms(prev => {
        const newSet = new Set(prev);
        if (newSet.has(expansionKey)) {
          newSet.delete(expansionKey);
        } else {
          newSet.add(expansionKey);
        }
        return newSet;
      });
    };

    // If system info is hidden, show as a user-style bubble (right-aligned)
    if (!showSystemInfo) {
      const showFormView = isExpanded && inlineFormSpec;
      const displayName = message.userName || user?.name;
      const firstName = displayName ? displayName.split(' ')[0] : 'User';

      return (
        <Box
          onMouseEnter={() => setIsBubbleHovered(true)}
          onMouseLeave={() => setIsBubbleHovered(false)}
        >
          {/* Header with name and timestamp - matches standard user message */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.5, gap: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                  {firstName}
                </Typography>
                {message.pending && (
                  <HourglassEmpty
                    sx={{
                      fontSize: '0.8rem',
                      animation: 'spin 2s linear infinite',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                )}
              </Box>
              {showTimestamp && (
                <Typography variant="caption" color="text.secondary">
                  {message.pending ? 'Sending...' : currentTimeString}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{
            p: 2,
            bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
            color: isDark ? '#e8eaed' : 'text.primary',
            borderRadius: 2,
            boxShadow: 'none',
            width: showFormView ? '100%' : 'fit-content',
            maxWidth: '80%',
            ml: 'auto',
            opacity: message.pending ? 0.9 : 1,
            animation: message.pending ? 'pulse 1.5s ease-in-out infinite' : 'none',
            '@keyframes pulse': {
              '0%': { opacity: 0.9 },
              '50%': { opacity: 0.6 },
              '100%': { opacity: 0.9 },
            },
          }}>
            {showFormView ? (
              <FormRenderer
                formSpec={inlineFormSpec}
                formData={inlineFormValues}
                readOnly={true}
                onFieldChange={() => { }}
              />
            ) : (
              <MessageContent
                content={getFormSummary(message.content) || 'Form submitted'}
                sx={{
                  bgcolor: 'transparent',
                  p: 0,
                  border: 'none',
                }}
              />
            )}
          </Box>
          {/* Hover controls - fixed height to prevent layout jumping */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.5, height: 28, gap: 0.5 }}>
            {message.error && (
              <>
                <Tooltip title={message.errorMessage || 'Failed to send'} placement="top">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}>
                    <ErrorOutline sx={{ fontSize: '0.9rem' }} />
                    <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 500 }}>Failed to send</Typography>
                  </Box>
                </Tooltip>
                <Tooltip title="Retry" placement="top">
                  <IconButton size="small" onClick={onRetryMessage} sx={{ color: 'error.main', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' } }}>
                    <Refresh sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {!message.error && (isBubbleHovered || hasNotes) && (
              <>
                {canAddNotes && !isChildMessage && (
                  <Tooltip title={hasNotes ? 'View notes' : 'Add note'} placement="top">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenNotesModal(message.originalIndex !== undefined ? message.originalIndex : index)}
                      sx={{
                        color: hasNotes ? 'primary.main' : 'text.secondary',
                        '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
                      }}
                    >
                      {hasNotes ? <Note sx={{ fontSize: '1.1rem' }} /> : <NoteAdd sx={{ fontSize: '1.1rem' }} />}
                    </IconButton>
                  </Tooltip>
                )}
                {inlineFormSpec && (
                  <Tooltip title={showFormView ? 'Show summary' : 'Show form'} placement="top">
                    <IconButton
                      size="small"
                      onClick={toggleExpand}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
                      }}
                    >
                      {showFormView ? <Subject sx={{ fontSize: '1.1rem' }} /> : <ViewList sx={{ fontSize: '1.1rem' }} />}
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{
        mb: 2,
        ml: (isChildMessage && showSystemInfo) ? 3 : 0,
        pl: (isChildMessage && showSystemInfo) ? 2 : 0,
        borderLeft: (isChildMessage && showSystemInfo) ? '3px solid' : 'none',
        borderLeftColor: (isChildMessage && showSystemInfo) ? 'secondary.main' : 'transparent',
      }}>
        {showTimestamp && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            {formatDate(message.timestamp)}
          </Typography>
        )}
        <Box
          onClick={toggleExpand}
          sx={{
            my: 2,
            p: 2,
            bgcolor: isDark ? 'grey.900' : 'grey.100',
            borderRadius: 2,
            cursor: formSpec ? 'pointer' : 'default',
            opacity: formSpec ? 1 : 0.6,
            '&:hover': formSpec ? {
              bgcolor: isDark ? 'grey.800' : 'grey.200',
            } : {},
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              flex: 1
            }}>
              <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                📋{' '}
              </Typography>
              {isAgent ? (
                <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                  Please fill out the below form on '{message.content}'
                </Typography>
              ) : (
                <MessageContent
                  content={message.content}
                  sx={{
                    display: 'inline',
                    fontSize: '0.875rem',
                    '& p': { display: 'inline', mb: 0 }
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {canAddNotes && !isChildMessage && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenNotesModal(message.originalIndex !== undefined ? message.originalIndex : index);
                  }}
                  onMouseEnter={() => setHoveredMessageIndex(index)}
                  onMouseLeave={() => setHoveredMessageIndex(null)}
                  sx={{
                    opacity: hasNotes || hoveredMessageIndex === index ? 1 : 0,
                    transition: 'opacity 0.2s',
                    color: hasNotes ? 'primary.main' : 'text.secondary'
                  }}
                >
                  <NoteAdd fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" sx={{ pointerEvents: 'none' }}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
          </Box>

          <Collapse in={isExpanded}>
            {formSpec && (
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <FormRenderer
                  formSpec={formSpec}
                  formData={formData || {}}
                  onFieldChange={() => { }} // No-op for read-only
                  readOnly={true}
                />
              </Box>
            )}
          </Collapse>
        </Box>
      </Box>
    );
  }

  // Special rendering for Knowledge-tagged messages (muted, like API messages)
  if (isKnowledgeMessage) {
    return (
      <Box
        sx={{
          mb: 1,
          ml: (isChildMessage && showSystemInfo) ? 3 : 0,
          pl: (isChildMessage && showSystemInfo) ? 2 : 0,
          borderLeft: (isChildMessage && showSystemInfo) ? '3px solid' : 'none',
          borderLeftColor: (isChildMessage && showSystemInfo) ? 'secondary.main' : 'transparent',
          px: 2,
        }}
      >
        {showTimestamp && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {formatDate(message.timestamp)}
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          Knowledge: {message.content?.substring(0, 120)}{message.content?.length > 120 ? '...' : ''}
        </Typography>
      </Box>
    );
  }

  // Special rendering for CaptureFact-tagged messages (muted, like API messages)
  if (isCaptureFactMessage) {
    return (
      <Box
        sx={{
          mb: 1,
          ml: (isChildMessage && showSystemInfo) ? 3 : 0,
          pl: (isChildMessage && showSystemInfo) ? 2 : 0,
          borderLeft: (isChildMessage && showSystemInfo) ? '3px solid' : 'none',
          borderLeftColor: (isChildMessage && showSystemInfo) ? 'secondary.main' : 'transparent',
          px: 2,
        }}
      >
        {showTimestamp && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {formatDate(message.timestamp)}
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          {message.content?.substring(0, 120)}{message.content?.length > 120 ? '...' : ''}
        </Typography>
      </Box>
    );
  }

  // Special rendering for EmailAction-tagged messages (expandable accordion like API/Memory)
  // Hide EmailAction messages when showSystemInfo is false
  if (isEmailAction) {
    if (!showSystemInfo) {
      return null;
    }
    return (
      <Box
        sx={{
          mb: 2,
          ml: (isChildMessage && showSystemInfo) ? 3 : 0,
          pl: (isChildMessage && showSystemInfo) ? 2 : 0,
          borderLeft: (isChildMessage && showSystemInfo) ? '3px solid' : 'none',
          borderLeftColor: (isChildMessage && showSystemInfo) ? 'secondary.main' : 'transparent',
        }}
      >
        <Accordion
          expanded={isExpanded}
          onChange={() => toggleMessageExpansion(index)}
          sx={{
            backgroundColor: theme.palette.mode === 'dark'
              ? 'var(--agentflow-message-agent)'
              : 'transparent',
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            '&:before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary
            sx={{
              minHeight: 'auto',
              p: 2,
              '& .MuiAccordionSummary-content': {
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                position: 'static',
                transform: 'none',
                marginLeft: 0,
              },
              '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                transform: 'rotate(180deg)',
              },
              '&.Mui-expanded': {
                minHeight: 'auto',
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, flex: 1 }}>
              {showTimestamp && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatDate(message.timestamp)}
                </Typography>
              )}
              <Typography variant="body2" sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
              }}>
                Drafted email: {message.content?.substring(0, 50)}{message.content?.length > 50 ? '...' : ''}
              </Typography>
            </Box>
            <ExpandMore sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
          </AccordionSummary>
          <AccordionDetails sx={{
            pt: 0,
            pb: 2,
            px: 2,
          }}>
            <Box sx={{
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              lineHeight: 1.4,
            }}>
              <MessageContent
                content={message.content}
                isUserMessage={false}
                onFormSubmit={handleFormSubmit}
                formDataByFormId={formDataByFormId}
                onFormFieldChange={handleFormFieldChange}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }

  // Standard rendering for non-Memory/Knowledge/EmailAction messages
  const isApiMessage = hasApiTag(message)

  return (
    <Box
      onMouseEnter={() => setIsBubbleHovered(true)}
      onMouseLeave={() => setIsBubbleHovered(false)}
    >
      <Box
        sx={{
          mb: 0,
          display: 'flex',
          flexDirection: (isUser || isExternal) ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          ml: (isChildMessage && showSystemInfo) ? 3 : 0, // Add left margin for child messages (only when showSystemInfo)
          pl: (isChildMessage && showSystemInfo) ? 2 : 0, // Add left padding for child messages (only when showSystemInfo)
          borderLeft: (isChildMessage && showSystemInfo) ? '3px solid' : 'none',
          borderLeftColor: (isChildMessage && showSystemInfo) ? 'secondary.main' : 'transparent',
        }}
      >
        <Box
          sx={{
            width: '100%',
          }}
        >
          {/* Child ticket indicator - show only for first message from each child agent AND when showSystemInfo is enabled */}
          {showChildChip && showSystemInfo && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 0.5,
                gap: 1,
              }}
            >
              <Chip
                icon={<Assignment sx={{ fontSize: '0.75rem' }} />}
                label={childTicketType || 'Child Ticket'}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  '& .MuiChip-icon': {
                    marginLeft: '4px',
                  },
                }}
              />
            </Box>
          )}

          {/* Only show header if not agent or if timestamp changed, but never for API messages (they use accordions) */}
          {((!isAgent && !isExternal) || showTimestamp) && !isApiMessage && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: (isUser || isExternal) ? 'flex-end' : 'flex-start',
                alignItems: 'center',
                mb: 0.5,
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: (isUser || isExternal) ? 'row-reverse' : 'row',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {/* Show name for user/external, hide for agent */}
                {(isUser || isExternal) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                      {(() => {
                        const role = effectiveRole?.toLowerCase();
                        if (role === 'user') {
                          // Use message.userName if available (from message metadata)
                          // Otherwise fall back to current user's name
                          const displayName = message.userName || user?.name;
                          if (displayName) {
                            // Extract first name only
                            return displayName.split(' ')[0];
                          }
                          return 'User';
                        } else if (role === 'external') {
                          return message.messageMetadata?.source === 'external-email' ? 'External User (Email)' : 'External User';
                        }
                        return effectiveRole;
                      })()}
                    </Typography>

                    {message.messageMetadata?.source === 'external-email' && (
                      <Email sx={{ ml: 0.5, fontSize: '0.9rem', color: 'secondary.main' }} />
                    )}
                    {message.pending && (
                      <HourglassEmpty
                        sx={{
                          ml: 0.5,
                          fontSize: '0.8rem',
                          animation: 'spin 2s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                          },
                        }}
                      />
                    )}
                  </Box>
                )}

                {/* Show timestamp only if it changed */}
                {showTimestamp && (
                  <Typography variant="caption" color="text.secondary">
                    {message.pending ? 'Sending...' : currentTimeString}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Wrap API-tagged messages in accordion */}
          {isApiMessage ? (
            <Accordion
              expanded={isExpanded}
              onChange={() => toggleMessageExpansion(index)}
              sx={{
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'var(--agentflow-message-agent)'
                  : 'transparent',
                border: 'none',
                borderRadius: 0,
                boxShadow: 'none',
                '&:before': {
                  display: 'none',
                },
              }}
            >
              <AccordionSummary
                sx={{
                  minHeight: 'auto',
                  p: 2,
                  '& .MuiAccordionSummary-content': {
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  },
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    position: 'static',
                    transform: 'none',
                    marginLeft: 0,
                  },
                  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                    transform: 'rotate(180deg)',
                  },
                  '&.Mui-expanded': {
                    minHeight: 'auto',
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, flex: 1 }}>
                  {showTimestamp && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {formatDate(message.timestamp)}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{
                    color: 'text.secondary',
                    fontStyle: 'italic',
                  }}>
                    Called API: {message.content?.substring(0, 50)}{message.content?.length > 50 ? '...' : ''}
                  </Typography>
                </Box>
                <ExpandMore sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
              </AccordionSummary>
              <AccordionDetails sx={{
                pt: 0,
                pb: 2,
                px: 2,
              }}>
                <MessageContent
                  content={message.content}
                  isUserMessage={isUser || isExternal}
                  onFormSubmit={handleFormSubmit}
                  formDataByFormId={formDataByFormId}
                  onFormFieldChange={handleFormFieldChange}
                />
              </AccordionDetails>
            </Accordion>
          ) : (
            <Box
              sx={{
                position: 'relative',
                backgroundColor: message.pending
                  ? 'grey.300'
                  : isUser || isExternal
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.03)'
                    : isAgent
                      ? theme.palette.mode === 'dark'
                        ? 'transparent'
                        : 'transparent'
                      : 'transparent',
                color: theme.palette.mode === 'dark' ? '#e8eaed' : 'text.primary',
                p: 2,
                borderRadius: isUser || isExternal ? 2 : 0,
                boxShadow: 'none',
                opacity: message.pending ? 0.9 : 1,
                animation: message.pending ? 'pulse 1.5s ease-in-out infinite' : 'none',
                border: 'none',
                width: (isUser || isExternal) && !isSubmittedForm ? 'fit-content' : '100%',
                maxWidth: isUser || isExternal ? '80%' : '100%',
                ml: isUser || isExternal ? 'auto' : 0,
                '@keyframes pulse': {
                  '0%': { opacity: 0.9 },
                  '50%': { opacity: 0.6 },
                  '100%': { opacity: 0.9 },
                },
              }}
            >
              <Box sx={{
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }}>
                <MessageContent
                  content={message.content}
                  isUserMessage={isUser || isExternal}
                  onFormSubmit={handleFormSubmit}
                  formDataByFormId={formDataByFormId}
                  onFormFieldChange={handleFormFieldChange}
                  isHistoricalQuickReply={isHistoricalQuickReply}
                  selectedReply={selectedReply}
                />
                {/* Inline image preview for uploaded image files */}
                <UploadedFilePreview content={message.content} ticket={ticket} />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      {/* Hover controls - fixed height to prevent layout jumping */}
      <Box sx={{ display: 'flex', justifyContent: (isUser || isExternal) ? 'flex-end' : 'flex-start', alignItems: 'center', mb: 2, height: 28, gap: 0.5 }}>
        {message.error && (
          <>
            <Tooltip title={message.errorMessage || 'Failed to send'} placement="top">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}>
                <ErrorOutline sx={{ fontSize: '0.9rem' }} />
                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 500 }}>Failed to send</Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Retry" placement="top">
              <IconButton size="small" onClick={onRetryMessage} sx={{ color: 'error.main', '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' } }}>
                <Refresh sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </Tooltip>
          </>
        )}
        {!message.error && (isBubbleHovered || hasNotes) && canAddNotes && !isChildMessage && (
          <Tooltip title={hasNotes ? 'View notes' : 'Add note'} placement="top">
            <IconButton
              size="small"
              onClick={() => handleOpenNotesModal(message.originalIndex !== undefined ? message.originalIndex : index)}
              sx={{
                color: hasNotes ? 'primary.main' : 'text.secondary',
                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
              }}
            >
              {hasNotes ? <Note sx={{ fontSize: '1.1rem' }} /> : <NoteAdd sx={{ fontSize: '1.1rem' }} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function - return true if props are equal (skip re-render)
  // Return false if props changed (re-render needed)

  // Check if message content or metadata changed
  if (prevProps.message.content !== nextProps.message.content) return false;
  if (prevProps.message.timestamp !== nextProps.message.timestamp) return false;
  if (prevProps.message.role !== nextProps.message.role) return false;
  if (prevProps.message.pending !== nextProps.message.pending) return false;
  if (prevProps.message.error !== nextProps.message.error) return false;

  // Check if message metadata changed (deep comparison for tags and other metadata)
  const prevMeta = JSON.stringify(prevProps.message.messageMetadata);
  const nextMeta = JSON.stringify(nextProps.message.messageMetadata);
  if (prevMeta !== nextMeta) return false;

  // Check if expansion state changed
  if (prevProps.isExpanded !== nextProps.isExpanded) return false;

  // Check if form data changed (deep comparison)
  const prevFormData = JSON.stringify(prevProps.formDataByFormId);
  const nextFormData = JSON.stringify(nextProps.formDataByFormId);
  if (prevFormData !== nextFormData) return false;

  // Check if previous message changed (affects timestamp display logic)
  const prevPrevMsg = prevProps.allMessages[prevProps.index - 1];
  const nextPrevMsg = nextProps.allMessages[nextProps.index - 1];
  if (prevPrevMsg?.timestamp !== nextPrevMsg?.timestamp) return false;
  if (prevPrevMsg?.role !== nextPrevMsg?.role) return false;

  // Check if notes-related props changed
  if (prevProps.canAddNotes !== nextProps.canAddNotes) return false;
  if (prevProps.hasNotes !== nextProps.hasNotes) return false;
  if (prevProps.hoveredMessageIndex !== nextProps.hoveredMessageIndex) return false;

  // Check if ticket changed (contains forms data for submitted forms)
  if (prevProps.ticket !== nextProps.ticket) return false;

  // Check if parentTicket changed (contains parent + child forms for submitted forms)
  if (prevProps.parentTicket !== nextProps.parentTicket) return false;

  // Check if expandedForms changed (Set reference changes when user expands/collapses)
  if (prevProps.expandedForms !== nextProps.expandedForms) return false;

  // Check if theme mode changed (light/dark toggle)
  if (prevProps.theme?.palette?.mode !== nextProps.theme?.palette?.mode) return false;

  // All checks passed - props are equal, skip re-render
  return true;
});

ConversationMessage.displayName = 'ConversationMessage';

export default ConversationMessage
